import * as Enumerable from "linq";
import { bindable, Disposable } from "aurelia-framework";
import { EventAggregator } from "aurelia-event-aggregator";
import { autoinject, transient } from "aurelia-dependency-injection";
import { ListDisplayHelper } from "../../../helpers/list-display-helper";
import { Messages } from "../../../configuration/event-messages.constants";

@autoinject()
@transient()
export class CiiRatingSelector {

    @bindable
    public selectedRatings: string[] = [];

    @bindable
    public selectedItems: string[] = []; //apply default filters

    @bindable
    public propertyName: string = "";

    @bindable
    public externalLabel: string;

    @bindable
    public startExternalLabel: string;

    @bindable
    public mobileTitle: string;

    @bindable
    public mobileDescription: string;

    @bindable
    public isMobile: boolean = false;

    @bindable
    public closePopUpOnClear: boolean = true;

    @bindable
    public ratings: CIIRatingItem[] =
        [
            new CIIRatingItem("A"),
            new CIIRatingItem("B"),
            new CIIRatingItem("C"),
            new CIIRatingItem("D"),
            new CIIRatingItem("E"),
            new CIIRatingItem("notCertified")
        ];

    public selectAll: boolean;
    public isApplied: boolean = false;
    private previousIsApplied: boolean;
    private previousSelectAll: boolean;
    private previousSelectedRatings: string[];
    private previousSelectedItems: string[];
    private previousRatings: CIIRatingItem[]
    private isOpen: boolean;
    subscriptionToDocumentClickEvent: Disposable;

    constructor(private element: Element, private eventAggregator: EventAggregator) {}

    attached() {
        setTimeout(() => {
            this.isOpen = true;
            this.previousRatings = structuredClone(this.ratings);
            this.previousIsApplied = this.isApplied;
            this.previousSelectAll = this.selectAll;
            this.previousSelectedRatings = this.selectedRatings;
            this.previousSelectedItems = this.selectedItems;
            this.subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup.bind(this));
        });
        this.setSelectedItems();
        this.updateExternalLabel();
    }

    detached() {
        if (this.subscriptionToDocumentClickEvent)
            this.subscriptionToDocumentClickEvent.dispose();
    }

    public selectAllChanged(value: boolean) {
        if (value) {
            this.ratings.forEach(x => x.isSelected = true);
        }
        else {
            this.ratings.forEach(x => x.isSelected = false);
        }
        this.updateSelectedRatings();
    }

    public updateSelectedRatings() {
        let selectedRatings = this.ratings.filter(x => x.isSelected);
        const selectedRatingWeights = [];

        if (selectedRatings.length == 0) {
            selectedRatings = this.ratings;
        }

        for (const item of selectedRatings) {

            if (item.isSelected) {
                selectedRatingWeights.push(item.rating);
            }
        }

        this.selectedRatings = Enumerable.from(selectedRatingWeights).distinct().toArray();
        this.updateSelectedItems();
        this.updateExternalLabel();

        if (this.ratings.filter(x => !x.isSelected).length > 0) {
            this.selectAll = false;
        }
    }

    public clear() {
        this.updateRatingModelsSelected(null);

        this.isApplied = false;
        this.selectAll = false;
        this.selectedRatings = [];
        this.selectedItems = [];

        if(this.closePopUpOnClear){
            setTimeout(() => {
                this.applyFilters(false);
            }, 120);   
        }

        this.updateExternalLabel();
    }

    public applyFilters(closeFilter: boolean = true, reset: boolean = false) {
        if (reset) {
            this.reset();
        }

        this.isApplied = Enumerable.from(this.ratings).any(x => x.isSelected);
        this.updateExternalLabel();
        const changeEvent: CustomEvent = new CustomEvent("apply-filters",
            {
                bubbles: true,
                detail: {
                    closeFilter: closeFilter,
                    filters: this.selectedRatings
                }
            });
        this.element.dispatchEvent(changeEvent);
    }

    updateSelectedItems() {
        this.selectedItems = [];

        if (this.selectAll) {
            this.selectedItems.push("selectAll");
        }

        //Add ratings to selected items array
        this.selectedItems = this.selectedItems.concat(this.ratings.filter(x => x.isSelected).map(x => x.rating));
    }

    setSelectedItems() {

        //Array of items that are not ratings
        const nonRatingItems: string[] = [
            "selectAll",
        ]

        if (this.selectedItems?.length > 0) {
            this.isApplied = true; //set to true since there are already items selected, to updated the label since it is checking for is applied

            //Select all is selected
            if (this.selectedItems.indexOf(nonRatingItems[0]) > -1) {
                this.selectAll = true;
            }
            else {
                const selectedRatings = this.selectedItems.filter(x => nonRatingItems.indexOf(x) == -1); //Array of the selected ratings
                this.updateRatingModelsSelected(selectedRatings);
            }
        }
    }

    updateRatingModelsSelected(selectedRatings: string[]) {
        if (!selectedRatings) {
            this.ratings.forEach(x => {
                x.isSelected = false;
            });
        }
        else {
            this.ratings.forEach(x => {
                x.isSelected = selectedRatings.indexOf(x.rating) > -1;
            });
        }
    }

    getSelectionDescription(isForMobile: boolean = false) {

        const ratings = this.ratings.map(x => { return {name: x.rating, isSelected: x.isSelected};});

        return isForMobile? ListDisplayHelper.getSelectableListToString(ratings)
            : (this.startExternalLabel ? `${this.startExternalLabel}` : `GHG Rating`);
    }

    updateExternalLabel() {
        if (Enumerable.from(this.ratings).any(x => x.isSelected)) {
            this.externalLabel = this.getSelectionDescription();
            this.mobileTitle = this.propertyName;
            this.mobileDescription = this.getSelectionDescription(true);
        }
        else {
            this.externalLabel = "";
            this.mobileTitle = this.propertyName;
            this.mobileDescription = "";
        }
    }

    closePopup(x: any) {
        if(this.isMobile) return;
        if (x && x.source == 'document' && this.isOpen) {
            this.isOpen = false;
            this.applyFilters(true, true) 
        }
    }

    reset() {
        this.ratings = this.previousRatings;
        this.isApplied = this.previousIsApplied;
        this.selectAll = this.previousSelectAll;
        this.selectedRatings = this.previousSelectedRatings;
        this.selectedItems = this.previousSelectedItems;
    }
}

export class CIIRatingItem {
    rating: string;
    isSelected?: boolean = false;

    constructor(rating: string) {
        this.rating = rating;
    }
}